import React, { useState, useRef } from 'react';
import { Camera } from 'lucide-react';
import { connect } from 'react-redux';
import { openMainModal } from '../../../store/actions/profileActions';
import { useOnboardingFlow } from '../../../hooks/onboardingV2/useOnboardingFlow.ts';
import defaultPersonImage from '../../../assets/images/userDefaultHelmet.png';
import defaultCompanyImage from '../../../assets/images/companyDefaultLogo.png';
import styles from './ProfilePhotoUploadV2.module.scss';

function compressImage(file: File, maxWidth: number = 800, quality: number = 0.7): Promise<string> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    let objectURL: string;
    img.onload = () => {
      try {
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        ctx?.drawImage(img, 0, 0, width, height);
        const compressedDataURL = canvas.toDataURL('image/jpeg', quality);
        canvas.width = 0;
        canvas.height = 0;
        if (objectURL) URL.revokeObjectURL(objectURL);
        resolve(compressedDataURL);
      } catch (e) {
        if (objectURL) URL.revokeObjectURL(objectURL);
        reject(e);
      }
    };
    img.onerror = () => {
      if (objectURL) URL.revokeObjectURL(objectURL);
      reject(new Error('Error al cargar la imagen'));
    };
    objectURL = URL.createObjectURL(file);
    img.src = objectURL;
  });
}

interface ProfilePhotoUploadV2Props {
  openMainModal: (config: any) => void;
}

const ProfilePhotoUploadV2: React.FC<ProfilePhotoUploadV2Props> = ({ openMainModal }) => {
  const { state, setUserImage } = useOnboardingFlow();
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const preview = state.userImage;
  const defaultSrc = state.accountType === 'COMPANY' ? defaultCompanyImage : defaultPersonImage;

  const showModal = (title: string, body: string) => {
    openMainModal({
      title,
      body,
      iconType: 'error',
      isSuccess: true,
      confirmHandler: null,
    });
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !(file instanceof File) || file.size <= 0) return;
    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      showModal('Formato de imagen no válido', 'Solo se permiten archivos JPEG o PNG');
      return;
    }
    compressImage(file, 800, 0.7)
      .then((base64) => {
        const max = 5 * 1024 * 1024;
        if (base64.length * 1.37 > max) return compressImage(file, 600, 0.5);
        return base64;
      })
      .then((base64) => {
        setUserImage(base64);
      })
      .catch(() => {
        showModal('Error al procesar imagen', 'Error al procesar la imagen. Intenta con otra imagen.');
      });
    e.target.value = '';
  };

  return (
    <div className={styles.wrapper}>
      <div
        className={styles.container}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
      >
        {preview ? (
          <img src={preview} alt="Foto de perfil" className={styles.preview} />
        ) : (
          <img src={defaultSrc} alt="Imagen por defecto" className={styles.defaultImg} />
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png"
          onChange={handleImageChange}
          className={styles.input}
          aria-label="Cambiar foto de perfil"
        />
        {isHovered && (
          <div className={styles.overlay}>
            <Camera size={24} />
            <span>Cambiar foto</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default connect(null, { openMainModal })(ProfilePhotoUploadV2);
